import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  ThemeIcon,
  Box,
  Timeline,
} from "@mantine/core";
import {
  IconToolsKitchen2,
  IconBrandInstagram,
  IconBrandX,
  IconRun,
  IconSun,
  IconBriefcase,
  IconClock,
  IconBed,
  IconInfoCircle,
} from "@tabler/icons-react";

const scheduleData = [
  {
    time: "12:00",
    activity: "Wake up",
    notes: "NO social media",
  },
  {
    time: "12:15–13:00",
    activity: "Sunlight walk",
  },
  {
    time: "13:00–13:45",
    activity: "Breakfast",
    food: "Eggs + oats",
    protein: "35g",
  },
  {
    time: "14:15–14:30",
    activity: "Walk to gym",
  },
  {
    time: "14:30–15:30",
    activity: "Gym",
  },
  {
    time: "15:30–15:45",
    activity: "Walk home",
  },
  {
    time: "15:45–16:30",
    activity: "Post-gym meal",
    food: "Skyr (300g) + banana",
    protein: "30g",
  },

  // --- WORK BLOCK 1 WITH MICRO-BREAKS ---
  {
    time: "16:30–17:55",
    activity: "Work block 1 — Focus Sprint",
  },
  {
    time: "17:55–18:05",
    activity: "Micro-break",
    notes: "Walk/stretch/hakken/water (5–7 min)",
  },
  {
    time: "18:05–19:30",
    activity: "Work block 1 — Focus Sprint 2",
  },

  {
    time: "19:30–19:45",
    activity: "Break",
    social_media: "X (15 min)",
  },
  {
    time: "19:45–20:30",
    activity: "Creative walk",
  },
  {
    time: "20:30–20:45",
    activity: "Social window",
    food: "Light snack if needed",
    protein: "10g",
    social_media: "X (15 min)",
  },
  {
    time: "20:45–21:05",
    activity: "Social window",
    social_media: "Instagram (20 min)",
  },
  {
    time: "22:00–00:00",
    activity: "Dinner + shower",
    food: "Chicken/beef + rice/veg",
    protein: "55g",
  },

  // --- WORK BLOCK 2 WITH MICRO-BREAKS ---
  {
    time: "00:00–01:30",
    activity: "Work block 2 — Focus Sprint",
  },
  {
    time: "01:35–03:00",
    activity: "Work block 2 — Focus Sprint 2",
  },

  {
    time: "03:30–04:00",
    activity: "Sleep",
  },
];

const getActivityIcon = (activity: string) => {
  const lower = activity.toLowerCase();
  if (lower.includes("sleep") || lower.includes("wake"))
    return <IconBed size={20} />;
  if (lower.includes("walk") || lower.includes("gym"))
    return <IconRun size={20} />;
  if (
    lower.includes("meal") ||
    lower.includes("breakfast") ||
    lower.includes("dinner") ||
    lower.includes("food")
  )
    return <IconToolsKitchen2 size={20} />;
  if (lower.includes("work")) return <IconBriefcase size={20} />;
  if (lower.includes("sunlight")) return <IconSun size={20} />;
  return <IconClock size={20} />;
};

export default function CalendarPage() {
  return (
    <Container size="md" py="xl">
      <Stack gap="xl">
        <Box>
          <Title
            order={1}
            fw={900}
            ta="center"
            mb="xs"
            style={{ letterSpacing: "-1px" }}
          >
            Daily Routine
          </Title>
          <Text c="dimmed" ta="center" size="lg" maw={500} mx="auto">
            A structured timeline for productivity, health, and balance.
          </Text>
        </Box>

        <Timeline active={-1} bulletSize={40} lineWidth={2}>
          {scheduleData.map((item, index) => (
            <Timeline.Item
              key={index}
              bullet={
                <ThemeIcon
                  size={40}
                  radius="xl"
                  color={
                    item.activity.toLowerCase().includes("work")
                      ? "blue"
                      : item.activity.toLowerCase().includes("gym")
                      ? "red"
                      : item.activity.toLowerCase().includes("meal")
                      ? "green"
                      : "gray"
                  }
                  variant="light"
                >
                  {getActivityIcon(item.activity)}
                </ThemeIcon>
              }
            >
              <Card withBorder shadow="sm" radius="md" padding="lg" mb="sm">
                <Group justify="space-between" align="flex-start" mb="xs">
                  <div>
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                      {item.time}
                    </Text>
                    <Text size="lg" fw={700} mt={4}>
                      {item.activity}
                    </Text>
                  </div>
                  {item.social_media && (
                    <Badge
                      variant="light"
                      color={
                        item.social_media.includes("Instagram")
                          ? "pink"
                          : "dark"
                      }
                      leftSection={
                        item.social_media.includes("Instagram") ? (
                          <IconBrandInstagram size={12} />
                        ) : (
                          <IconBrandX size={12} />
                        )
                      }
                    >
                      {item.social_media}
                    </Badge>
                  )}
                </Group>

                {(item.food || item.protein) && (
                  <Card.Section inheritPadding py="xs" withBorder>
                    <Group gap="xs">
                      <ThemeIcon color="green" variant="light" size="sm">
                        <IconToolsKitchen2 size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                        {item.food || "Meal"}
                      </Text>
                      {item.protein && (
                        <Badge color="green" variant="outline" size="sm">
                          {item.protein} protein
                        </Badge>
                      )}
                    </Group>
                  </Card.Section>
                )}

                {item.notes && (
                  <Card.Section
                    inheritPadding
                    py="xs"
                    withBorder
                    mt="xs"
                    style={{ backgroundColor: "#fffbe0" }}
                  >
                    <Group gap="xs">
                      <ThemeIcon color="yellow" variant="light" size="sm">
                        <IconInfoCircle size={12} />
                      </ThemeIcon>
                      <Text size="sm" c="yellow.9" fw={500} style={{ flex: 1 }}>
                        {item.notes}
                      </Text>
                    </Group>
                  </Card.Section>
                )}
              </Card>
            </Timeline.Item>
          ))}
        </Timeline>
      </Stack>
    </Container>
  );
}
